import React, { createContext, useContext, useState, useCallback } from 'react';
import { FaCheck, FaTimes, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({} as ToastContextValue);

const COLORS: Record<ToastType, { bg: string; icon: React.ReactNode; accent: string }> = {
  success: { bg: '#16a34a', accent: '#15803d', icon: <FaCheck /> },
  error: { bg: '#dc2626', accent: '#b91c1c', icon: <FaTimes /> },
  warning: { bg: '#f59e0b', accent: '#d97706', icon: <FaExclamationTriangle /> },
  info: { bg: '#3b82f6', accent: '#2563eb', icon: <FaInfoCircle /> },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => remove(id), type === 'error' || type === 'warning' ? 5000 : 3000);
  }, [remove]);

  const api: ToastContextValue = {
    showToast,
    success: (m) => showToast(m, 'success'),
    error: (m) => showToast(m, 'error'),
    warning: (m) => showToast(m, 'warning'),
    info: (m) => showToast(m, 'info'),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Stack de toasts */}
      <div style={{position:'fixed',top:16,left:'50%',transform:'translateX(-50%)',zIndex:30001,display:'flex',flexDirection:'column',gap:8,maxWidth:'92vw',width:'auto',pointerEvents:'none'}}>
        {toasts.map(t => {
          const c = COLORS[t.type];
          return (
            <div key={t.id} onClick={() => remove(t.id)} style={{
              display:'flex',alignItems:'center',gap:10,
              background:c.bg,color:'#fff',
              padding:'10px 16px 10px 14px',borderRadius:10,
              boxShadow:'0 6px 24px rgba(0,0,0,0.25)',
              fontSize:14,fontWeight:600,
              borderLeft:`4px solid ${c.accent}`,
              minWidth:240,maxWidth:420,
              animation:'toastIn 0.25s cubic-bezier(.4,1.3,.6,1)',
              pointerEvents:'auto',cursor:'pointer',
              wordBreak:'break-word'
            }}>
              <span style={{fontSize:16,flexShrink:0,display:'flex'}}>{c.icon}</span>
              <span style={{flex:1}}>{t.message}</span>
              <span style={{opacity:0.7,fontSize:14,cursor:'pointer',flexShrink:0}}><FaTimes /></span>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
